import React, { useEffect, useRef, useCallback } from 'react'
import {
  Button,
  CircularProgress,
  Fab,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import { CameraIcon } from '../../../../icons/CameraIcon'
import { useModal, useManualCamera } from '../../../../shared'
import { CameraModalBody } from '../../../../components/templates/CameraModalBody'
import { localStorageWrapper } from '../../../../helpers/localStorage'
import { useCreatePrescriptionMutation } from '../mutations/useCreatePrescriptionMutation'

/**
 * カメラモーダル hook（TanStack Query版）
 *
 * カメラで処方箋画像を撮影し、サーバーにアップロードする。
 */
export const useCamera = () => {
  const theme = useTheme()
  const isActivatedPhoto =
    localStorageWrapper.getItem('is_activated_photo') || false
  const { CameraWithButton, images, resetImage, activateCamera } =
    useManualCamera(1000, 1500)

  const createPrescriptionMutation = useCreatePrescriptionMutation()

  // closeCameraModal をrefで保持（宣言前参照を回避）
  const closeCameraModalRef = useRef<() => void>(() => {})

  const submitImages = useCallback(
    async (imagesToSubmit: string[]) => {
      resetImage()
      closeCameraModalRef.current()
      await createPrescriptionMutation.mutateAsync(imagesToSubmit)
    },
    [resetImage, createPrescriptionMutation]
  )

  const SubmitButton = (
    <Button
      variant="contained"
      disabled={images.length === 0}
      onClick={() => submitImages(images)}
    >
      送信
    </Button>
  )

  const {
    renderModal: renderCameraModal,
    openModal: openCameraModal,
    closeModal: closeCameraModal,
    isOpen: isCameraModalOpen,
  } = useModal(
    <CameraModalBody
      CameraComponent={CameraWithButton}
      SubmitButton={SubmitButton}
    />,
    'center'
  )

  // closeCameraModal をrefに保存
  useEffect(() => {
    closeCameraModalRef.current = closeCameraModal
  }, [closeCameraModal])

  useEffect(() => {
    window.requestAnimationFrame(activateCamera)
  }, [isCameraModalOpen, activateCamera])

  const OpenButton = isActivatedPhoto && (
    <Fab
      sx={{
        left: 20,
        bottom: 20,
        position: 'fixed',
        color: '#fff',
        backgroundColor: theme.palette.primary.dark, // theme を適切に定義してください
        zIndex: 1300,
      }}
      onClick={openCameraModal}
    >
      <CameraIcon />
    </Fab>
  )

  const DuringOcrOverlay = createPrescriptionMutation.isPending && (
    <Stack
      sx={{
        zIndex: 1310,
        position: 'absolute',
        bgcolor: 'rgb(0,0,0, 0.4)',
        width: '100%',
        height: '100%',
        justifyContent: 'center',
        alignItems: 'center',
      }}
      spacing={2}
    >
      <CircularProgress style={{ width: '100px', height: '100px' }} />
      <Typography sx={{ color: '#fff', fontSize: '24px' }}>
        処理実行中...
      </Typography>
    </Stack>
  )

  return { renderCameraModal, OpenButton, DuringOcrOverlay }
}
